/**
 * Loan Car Allocation Slice for Wadakaroo
 *
 * Manages state for loan car allocations on bookings:
 * - Allocations keyed by bookingUuid (loaded on demand when a booking is opened)
 * - Available cars search results (transient — reset on each search)
 * - CRUD operations: allocate, update, remove
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getAvailableLoanCarsApi,
    getBookingAllocationsApi,
    allocateLoanCarApi,
    updateAllocationApi,
    removeAllocationApi,
} from '../../services/loanCarAllocationApi';

// ============================================
// Async Thunks - Read
// ============================================

/**
 * Load all loan car allocations for a booking.
 * Keyed by bookingUuid in state so multiple bookings can be cached.
 */
export const fetchBookingAllocations = createAsyncThunk(
    'loanCarAllocations/fetchForBooking',
    async (bookingUuid, { rejectWithValue }) => {
        try {
            const allocations = await getBookingAllocationsApi(bookingUuid);
            return { bookingUuid, allocations };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch loan car allocations');
        }
    }
);

/**
 * Search for loan cars available during a time window.
 * @param {{ start: string, end: string, excludeAllocation?: string }} params
 */
export const searchAvailableLoanCars = createAsyncThunk(
    'loanCarAllocations/searchAvailable',
    async ({ start, end, excludeAllocation = null }, { rejectWithValue }) => {
        try {
            return await getAvailableLoanCarsApi(start, end, excludeAllocation);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to search available loan cars');
        }
    }
);

// ============================================
// Async Thunks - CRUD
// ============================================

/**
 * Allocate a loan car to a booking.
 * @param {{ bookingUuid: string, data: { loanCarUuid, allocationStart, allocationEnd, notes } }} params
 */
export const allocateLoanCar = createAsyncThunk(
    'loanCarAllocations/allocate',
    async ({ bookingUuid, data }, { rejectWithValue }) => {
        try {
            const allocation = await allocateLoanCarApi(bookingUuid, data);
            return { bookingUuid, allocation };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to allocate loan car');
        }
    }
);

/**
 * Update an existing allocation's window or notes.
 * @param {{ bookingUuid: string, allocationUuid: string, data: { allocationStart, allocationEnd, notes } }} params
 */
export const updateLoanCarAllocation = createAsyncThunk(
    'loanCarAllocations/update',
    async ({ bookingUuid, allocationUuid, data }, { rejectWithValue }) => {
        try {
            const allocation = await updateAllocationApi(bookingUuid, allocationUuid, data);
            return { bookingUuid, allocation };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update loan car allocation');
        }
    }
);

/**
 * Remove an allocation from a booking.
 * @param {{ bookingUuid: string, allocationUuid: string }} params
 */
export const removeLoanCarAllocation = createAsyncThunk(
    'loanCarAllocations/remove',
    async ({ bookingUuid, allocationUuid }, { rejectWithValue }) => {
        try {
            await removeAllocationApi(bookingUuid, allocationUuid);
            return { bookingUuid, allocationUuid };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to remove loan car allocation');
        }
    }
);

// ============================================
// Initial State
// ============================================

const initialState = {
    // Allocations per booking: { [bookingUuid]: LoanCarAllocation[] }
    allocationsByBooking: {},

    // Per-booking loading/error state: { [bookingUuid]: { loading, error } }
    loadingByBooking: {},

    // Available cars search (transient — reset on each new search)
    availableCars: [],
    availableCarsLoading: false,
    availableCarsError: null,

    // UI state
    showAllocationForm: false,
    editingAllocation: null,     // the allocation object being edited, or null for new

    // Operation states (allocate / update / remove)
    operationLoading: false,
    operationError: null,
    operationSuccess: null,
};

// ============================================
// Slice
// ============================================

const loanCarAllocationSlice = createSlice({
    name: 'loanCarAllocations',
    initialState,
    reducers: {
        // ── Form UI ──────────────────────────────────────────────

        openAllocationForm: (state, action) => {
            // Pass an existing allocation to edit, or null/undefined to create new
            state.showAllocationForm = true;
            state.editingAllocation = action.payload || null;
            state.availableCars = [];
            state.availableCarsError = null;
            state.operationError = null;
            state.operationSuccess = null;
        },
        closeAllocationForm: (state) => {
            state.showAllocationForm = false;
            state.editingAllocation = null;
            state.availableCars = [];
            state.availableCarsError = null;
        },

        // ── Available cars ────────────────────────────────────────

        clearAvailableCars: (state) => {
            state.availableCars = [];
            state.availableCarsError = null;
        },

        // ── Operation status ──────────────────────────────────────

        clearOperationStatus: (state) => {
            state.operationError = null;
            state.operationSuccess = null;
        },

        // ── Cache management ──────────────────────────────────────

        /** Evict cached allocations for a booking (e.g. when the booking detail modal closes). */
        evictBookingAllocations: (state, action) => {
            const bookingUuid = action.payload;
            delete state.allocationsByBooking[bookingUuid];
            delete state.loadingByBooking[bookingUuid];
        },
    },
    extraReducers: (builder) => {
        // ==================== Fetch allocations for a booking ====================
        builder
            .addCase(fetchBookingAllocations.pending, (state, action) => {
                const bookingUuid = action.meta.arg;
                state.loadingByBooking[bookingUuid] = { loading: true, error: null };
            })
            .addCase(fetchBookingAllocations.fulfilled, (state, action) => {
                const { bookingUuid, allocations } = action.payload;
                state.allocationsByBooking[bookingUuid] = allocations;
                state.loadingByBooking[bookingUuid] = { loading: false, error: null };
            })
            .addCase(fetchBookingAllocations.rejected, (state, action) => {
                const bookingUuid = action.meta.arg;
                state.loadingByBooking[bookingUuid] = { loading: false, error: action.payload };
            })

            // ==================== Search available cars ====================
            .addCase(searchAvailableLoanCars.pending, (state) => {
                state.availableCarsLoading = true;
                state.availableCarsError = null;
                state.availableCars = [];
            })
            .addCase(searchAvailableLoanCars.fulfilled, (state, action) => {
                state.availableCarsLoading = false;
                state.availableCars = action.payload;
            })
            .addCase(searchAvailableLoanCars.rejected, (state, action) => {
                state.availableCarsLoading = false;
                state.availableCarsError = action.payload;
            })

            // ==================== Allocate loan car ====================
            .addCase(allocateLoanCar.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(allocateLoanCar.fulfilled, (state, action) => {
                const { bookingUuid, allocation } = action.payload;
                state.operationLoading = false;
                state.operationSuccess = 'Loan car allocated successfully';
                state.showAllocationForm = false;
                state.editingAllocation = null;
                state.availableCars = [];
                // Append to cached list for this booking
                if (state.allocationsByBooking[bookingUuid]) {
                    state.allocationsByBooking[bookingUuid].push(allocation);
                } else {
                    state.allocationsByBooking[bookingUuid] = [allocation];
                }
            })
            .addCase(allocateLoanCar.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Update allocation ====================
            .addCase(updateLoanCarAllocation.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateLoanCarAllocation.fulfilled, (state, action) => {
                const { bookingUuid, allocation } = action.payload;
                state.operationLoading = false;
                state.operationSuccess = 'Loan car allocation updated';
                state.showAllocationForm = false;
                state.editingAllocation = null;
                state.availableCars = [];
                const list = state.allocationsByBooking[bookingUuid];
                if (list) {
                    const idx = list.findIndex(a => a.uuid === allocation.uuid);
                    if (idx !== -1) list[idx] = allocation;
                }
            })
            .addCase(updateLoanCarAllocation.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Remove allocation ====================
            .addCase(removeLoanCarAllocation.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(removeLoanCarAllocation.fulfilled, (state, action) => {
                const { bookingUuid, allocationUuid } = action.payload;
                state.operationLoading = false;
                state.operationSuccess = 'Loan car allocation removed';
                const list = state.allocationsByBooking[bookingUuid];
                if (list) {
                    state.allocationsByBooking[bookingUuid] =
                        list.filter(a => a.uuid !== allocationUuid);
                }
            })
            .addCase(removeLoanCarAllocation.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            });
    },
});

// ============================================
// Selectors
// ============================================

/** Allocations for a specific booking (empty array if not yet loaded). */
export const selectAllocationsByBooking = (state, bookingUuid) =>
    state.loanCarAllocations.allocationsByBooking[bookingUuid] ?? [];

/** Loading/error state for a specific booking's allocations. */
export const selectAllocationLoadingForBooking = (state, bookingUuid) =>
    state.loanCarAllocations.loadingByBooking[bookingUuid] ?? { loading: false, error: null };

/** Available cars from the last search. */
export const selectAvailableCars = (state) => state.loanCarAllocations.availableCars;
export const selectAvailableCarsLoading = (state) => state.loanCarAllocations.availableCarsLoading;
export const selectAvailableCarsError = (state) => state.loanCarAllocations.availableCarsError;

/** Form UI state. */
export const selectShowAllocationForm = (state) => state.loanCarAllocations.showAllocationForm;
export const selectEditingAllocation = (state) => state.loanCarAllocations.editingAllocation;

/** Operation state. */
export const selectAllocationOperationLoading = (state) => state.loanCarAllocations.operationLoading;
export const selectAllocationOperationError = (state) => state.loanCarAllocations.operationError;
export const selectAllocationOperationSuccess = (state) => state.loanCarAllocations.operationSuccess;

// ============================================
// Exports
// ============================================

export const {
    openAllocationForm,
    closeAllocationForm,
    clearAvailableCars,
    clearOperationStatus,
    evictBookingAllocations,
} = loanCarAllocationSlice.actions;

export default loanCarAllocationSlice.reducer;